import { useKindeFeatureFlags, useTranslation } from "@next-platform/client-common-core";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSortBy } from "react-instantsearch";

import { TYPESENSE_TEXT_MATCH_SORT_PREFIX } from "@/constants";

import Dropdown from "../../common/Dropdown";

type SharedChatbotsSortDropdownProps = {
  baseIndexName: string;
  currentRefinement: string;
  setCurrentCategory: (value: string) => void;
};

const SORT_SUFFIX_TO_CATEGORY: Record<string, string> = {
  "num_messages_24h:desc": "trending",
  "rating_score:desc": "top_rated",
  "num_messages:desc": "popular",
  "createdAt:desc": "latest",
};

const getCategoryFromSortValue = (sortValue: string): string => {
  const matchedSuffix = Object.keys(SORT_SUFFIX_TO_CATEGORY).find((suffix) => sortValue.includes(suffix));
  return matchedSuffix ? SORT_SUFFIX_TO_CATEGORY[matchedSuffix] : "";
};

export default function SharedChatbotsSortDropdown({ baseIndexName, currentRefinement, setCurrentCategory }: SharedChatbotsSortDropdownProps) {
  const { hasAccessToV2 } = useKindeFeatureFlags();
  const { t } = useTranslation();

  const sortItems = useMemo(
    () => [
      { value: `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},num_messages_24h:desc`, label: t("search:sortBy.trending") },
      { value: `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},rating_score:desc`, label: t("search:sortBy.topRated") },
      { value: `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},num_messages:desc`, label: t("search:sortBy.popular") },
      { value: `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX},createdAt:desc`, label: t("search:sortBy.latest") },
    ],
    [t, baseIndexName],
  );

  const sortByProps = { items: sortItems, initialIndex: currentRefinement };
  const { currentRefinement: currentRefinementSortBy, options, refine: refineSortBy } = useSortBy(sortByProps);

  const [stateCurrentValue, setStateCurrentValue] = useState<string | null>(null);

  const getLabel = useCallback(
    (value: string) => {
      const sortOption = options?.find((option) => option.value === value);
      return sortOption ? sortOption.label : null;
    },
    [options],
  );

  useEffect(() => {
    if (currentRefinementSortBy) {
      setStateCurrentValue(currentRefinementSortBy);
      setCurrentCategory(getCategoryFromSortValue(currentRefinementSortBy));
    }
  }, [currentRefinementSortBy, setCurrentCategory]);

  const handleSortChange = (sortOption: { value: string }) => {
    refineSortBy(sortOption.value);
    setStateCurrentValue(sortOption.value);
  };

  const currentLabel = stateCurrentValue ? getLabel(stateCurrentValue) : t("search:sortBy.popular");

  return (
    <Dropdown
      currentValue={currentLabel || t("search:sortBy.popular")}
      hasAccessToV2={hasAccessToV2}
      listItems={sortItems.map((sortOption) => ({
        label: sortOption.label,
        value: sortOption.value,
        onPress: () => handleSortChange(sortOption),
      }))}
      iconEnd={IconVariants.CHEVRON_DOWN}
    />
  );
}
